// Self-contained "Best times to post" widget. Renders the discriminated
// union returned by the API: an `insufficient` branch with actionable
// guidance, or a populated slots list. Per §6.3.3 acceptance criteria
// AC #2 we ALWAYS show both `relativeScore` and `sampleSize` — never a
// fake confidence percent.

import type { OptimalSlotsResult } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import { slotHeader, slotSampleLabel } from "../lib/format";
import { OPTIMAL_SLOTS_STRINGS } from "../lib/strings";

export interface OptimalSlotsPanelProps {
  result: OptimalSlotsResult;
  className?: string;
}

export function OptimalSlotsPanel({ result, className }: OptimalSlotsPanelProps) {
  return (
    <section
      aria-labelledby="optimal-slots-title"
      className={cn("flex flex-col gap-2", className)}
    >
      <header>
        <h2 id="optimal-slots-title" className="text-sm font-semibold">
          {OPTIMAL_SLOTS_STRINGS.title}
        </h2>
        <p className="text-xs text-muted-foreground">
          {OPTIMAL_SLOTS_STRINGS.subtitle}
        </p>
      </header>
      {result.insufficient ? (
        <InsufficientPanel result={result} />
      ) : (
        <SufficientPanel result={result} />
      )}
    </section>
  );
}

function InsufficientPanel({
  result,
}: {
  result: Extract<OptimalSlotsResult, { insufficient: true }>;
}) {
  // Build a friendly shortfall list from whichever fields are non-zero —
  // empty arrays are skipped so the line stays clean for owners who only
  // need ONE more thing.
  const shortfalls: string[] = [];
  if (result.daysRemaining > 0) {
    shortfalls.push(OPTIMAL_SLOTS_STRINGS.shortfall.days(result.daysRemaining));
  }
  if (result.postsRemaining > 0) {
    shortfalls.push(
      OPTIMAL_SLOTS_STRINGS.shortfall.posts(result.postsRemaining),
    );
  }
  if (result.slotsShort > 0) {
    shortfalls.push(OPTIMAL_SLOTS_STRINGS.shortfall.slots(result.slotsShort));
  }
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-dashed bg-background p-4 text-sm"
      role="status"
    >
      <p className="font-medium">{OPTIMAL_SLOTS_STRINGS.empty.title}</p>
      <p className="text-muted-foreground">{OPTIMAL_SLOTS_STRINGS.empty.body}</p>
      <p className="text-xs text-muted-foreground">
        {OPTIMAL_SLOTS_STRINGS.shortfall.so_far(result.totalAdminPosts)}
      </p>
      {shortfalls.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {shortfalls.map((s) => (
            <li key={s} className="flex items-center gap-2">
              <span aria-hidden="true">•</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SufficientPanel({
  result,
}: {
  result: Extract<OptimalSlotsResult, { insufficient: false }>;
}) {
  if (result.topSlots.length === 0) {
    // Defensive — backend may report sufficient with empty topSlots if
    // every slot dropped to 0 engagement. Render the empty hint without
    // pretending to recommend nothing.
    return (
      <div
        className="rounded-lg border border-dashed bg-background p-4 text-sm text-muted-foreground"
        role="status"
      >
        {OPTIMAL_SLOTS_STRINGS.empty.body}
      </div>
    );
  }
  return (
    <ol className="flex flex-col gap-2">
      {result.topSlots.map((slot, idx) => (
        <li
          key={slot.hourOfWeek}
          className="flex items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
              {idx + 1}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="text-sm font-medium tabular-nums">
                {slotHeader(slot.dayOfWeek, slot.hour)}
              </span>
              <span className="text-xs text-muted-foreground">
                {slotSampleLabel(slot.sampleSize)}
              </span>
            </div>
          </div>
          <div className="flex flex-none flex-col items-end text-xs tabular-nums text-muted-foreground">
            <span>
              <span className="font-semibold text-foreground">
                {slot.relativeScore}
              </span>{" "}
              / 100
            </span>
            <span>{OPTIMAL_SLOTS_STRINGS.scoreLabel}</span>
          </div>
        </li>
      ))}
      <li className="text-[11px] text-muted-foreground">
        {OPTIMAL_SLOTS_STRINGS.noteRelativeAndSample}
      </li>
    </ol>
  );
}
